import { makeAutoObservable } from 'mobx';
import defaultsDeep from 'lodash/fp/defaultsDeep';

import {
  CreateEntitySubscriptionInfo,
  EntitySubscriptionInfo,
} from '../interfaces/entitySubscription';
import { FilterStore } from '../../shared/util-stores';
import {
  EntitySubscriptionsFilterFormValue,
  EntitySubscriptionsSortField,
  EntitySubscriptionsGeneralFilter,
} from '../interfaces/entitySubscriptionFilters';
import { SOLUTION_TYPE_FILTER } from '../../shared/constants/user-info/solutionType';
import { entitySubscriptionService } from '../services/entitySubscriptionService';
import { updateItemByField } from '../../shared/utils/arrayUtils';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { SpringFiltration } from '../../shared/interfaces/SpringFiltration';
import { paginationSmallListLimit } from '../../shared/constants/systemConstants';
import { userRoles } from '../../shared/constants/userRoles';

type EntitySubscriptionsFilterInfo = SpringFiltration<
  EntitySubscriptionsFilterFormValue,
  EntitySubscriptionsSortField
>;

type DefaultGeneralFilter = Partial<EntitySubscriptionsGeneralFilter>;

class SubscriptionsManagementStore {
  private entityId: number | null = null;
  private defaultGeneralFilter: DefaultGeneralFilter;
  subscriptionsList: EntitySubscriptionInfo[] = [];
  filterMethods: FilterStore<
    EntitySubscriptionsFilterFormValue,
    EntitySubscriptionsSortField
  >;

  constructor(defaultGeneralFilter: DefaultGeneralFilter = {}) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.defaultGeneralFilter = defaultGeneralFilter;
    this.filterMethods = new FilterStore<
      EntitySubscriptionsFilterFormValue,
      EntitySubscriptionsSortField
    >(this.handleGetSubscriptionsList, {
      elementsPerPage: paginationSmallListLimit,
    });
  }

  setDefaultValues() {
    this.entityId = null;
    this.subscriptionsList = [];
    this.filterMethods.setDefaultValues();
  }

  private async handleGetSubscriptionsList(filterInfo: EntitySubscriptionsFilterInfo) {
    if (!this.entityId) {
      throw new Error('entityId is not set');
    }

    // Merge default filter params with filterInfo (filterInfo can override defaults)
    // defaultsDeep function from lodash/fp is not mutating an original object
    const updatedFilterInfo = defaultsDeep(
      {
        filters: {
          generalFilter: this.defaultGeneralFilter,
        },
      },
      filterInfo,
    );

    const data = await entitySubscriptionService.getFilteredEntitySubscriptions(
      this.entityId,
      updatedFilterInfo,
    );

    const { content } = data;

    this.setSubscriptionsList(content);

    return data;
  }

  async getSubscriptionsList(entityId: number) {
    this.setEntityId(entityId);
    return this.filterMethods.getData();
  }

  async createEntitySubscription(subscription: CreateEntitySubscriptionInfo) {
    if (!this.entityId) {
      throw new Error('entityId is not set');
    }

    await entitySubscriptionService.createEntitySubscription(this.entityId, subscription);
    await this.getSubscriptionsList(this.entityId);
  }

  async changeActivationStatus(subscriptionId: number, activate: boolean) {
    if (!this.entityId) {
      throw new Error('entityId is not set');
    }

    const changedSubscription = await entitySubscriptionService.changeActivationStatus(
      this.entityId,
      subscriptionId,
      activate,
    );

    this.updateSubscription(changedSubscription);
  }

  async changeDisplayToUserStatus(subscriptionId: number, displayToUser: boolean) {
    if (!this.entityId) {
      throw new Error('entityId is not set');
    }

    const changedSubscription = await entitySubscriptionService.changeDisplayToUserStatus(
      this.entityId,
      subscriptionId,
      displayToUser,
    );

    this.updateSubscription(changedSubscription);
  }

  private setEntityId(entityId: number) {
    this.entityId = entityId;
  }

  private updateSubscription(changedSubscription: EntitySubscriptionInfo) {
    const updatedSubscriptionsList = updateItemByField(
      this.subscriptionsList,
      changedSubscription,
      { id: changedSubscription.id },
    );

    this.setSubscriptionsList(updatedSubscriptionsList);
  }

  private setSubscriptionsList(subscriptions: EntitySubscriptionInfo[]) {
    this.subscriptionsList = subscriptions;
  }
}

const adminSubscriptionsManagementStore = new SubscriptionsManagementStore({
  ownerRoleType: userRoles.ADMIN,
  solutionType: SOLUTION_TYPE_FILTER.NUTRITION,
});
resetAllStore.addResetMethod(adminSubscriptionsManagementStore.setDefaultValues);

const superSubscriptionsManagementStore = new SubscriptionsManagementStore({
  ownerRoleType: userRoles.SUPERUSER,
  solutionType: SOLUTION_TYPE_FILTER.NUTRITION,
});
resetAllStore.addResetMethod(superSubscriptionsManagementStore.setDefaultValues);

//We are using one list of subscriptions for scan IQ, so not needed to pass ownerRoleType to store and filter subscriptions by this field
const scanIqSubscriptionsManagementStore = new SubscriptionsManagementStore({
  solutionType: SOLUTION_TYPE_FILTER.SCAN_IQ,
});
resetAllStore.addResetMethod(scanIqSubscriptionsManagementStore.setDefaultValues);

export {
  adminSubscriptionsManagementStore,
  superSubscriptionsManagementStore,
  scanIqSubscriptionsManagementStore,
  SubscriptionsManagementStore,
};
